/**
 * Parse / repair `ask_user_question` tool args.
 *
 * Some model/provider paths leak Anthropic-style XML parameter markup into the
 * `question` string instead of emitting structured `context` / `options`
 * fields, e.g.:
 *
 *   Which vendor_kind should stick?</question>
 *   <parameter name="context">…</context>
 *   <parameter name="options">["a", "b"]</parameter>
 *
 * Without repair the UI OpenQuestion card renders that blob as plain text and
 * never shows option pills. This helper is shared by the executor (so polluted
 * rows never persist) and the ingest questions transform (so already-written
 * rows still render as cards).
 */

export interface AskUserQuestionFields {
  question: string
  options?: string[]
  context?: string
}

const LEAKED_TOOL_XML_RE = /<\/question\s*>|<parameter\s+name\s*=/i

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/** True when `question` looks like leaked XML tool-parameter markup. */
export function hasLeakedAskUserQuestionXml(question: string): boolean {
  return LEAKED_TOOL_XML_RE.test(question)
}

/**
 * Coerce an `options` field that may arrive as `string[]`, a JSON array
 * string, or a JSON-ish array with unescaped newlines inside quoted values.
 */
export function coerceAskUserQuestionOptions(value: unknown): string[] | undefined {
  if (Array.isArray(value)) {
    const opts = value
      .filter((entry): entry is string => typeof entry === 'string')
      .map((entry) => entry.replace(/\s+/g, ' ').trim())
      .filter(Boolean)
    return opts.length > 0 ? opts : undefined
  }
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined

  if (trimmed.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(trimmed)
      return coerceAskUserQuestionOptions(parsed)
    } catch {
      // Models often wrap long option strings across lines without escaping
      // the newlines — extract quoted segments tolerantly.
      const opts: string[] = []
      const re = /"((?:\\.|[^"\\])*)"/g
      let match: RegExpExecArray | null
      while ((match = re.exec(trimmed)) !== null) {
        const raw = match[1] ?? ''
        try {
          opts.push(JSON.parse(`"${raw}"`).replace(/\s+/g, ' ').trim())
        } catch {
          opts.push(raw.replace(/\\"/g, '"').replace(/\\n/g, '\n').replace(/\s+/g, ' ').trim())
        }
      }
      const cleaned = opts.filter(Boolean)
      return cleaned.length > 0 ? cleaned : undefined
    }
  }

  return [trimmed.replace(/\s+/g, ' ').trim()].filter(Boolean)
}

function extractXmlParameter(blob: string, name: string): string | undefined {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  // Prefer a closed parameter block. Closing tag may be </parameter> or the
  // short form </name> (seen in the wild on leaked context blocks).
  const closed = new RegExp(
    `<parameter\\s+name\\s*=\\s*["']${escaped}["']\\s*>([\\s\\S]*?)(?:</parameter\\s*>|</${escaped}\\s*>)`,
    'i'
  )
  const closedMatch = blob.match(closed)
  if (closedMatch?.[1] !== undefined) return closedMatch[1].trim()

  // Unclosed / truncated: take until the next parameter or end of string.
  const open = new RegExp(
    `<parameter\\s+name\\s*=\\s*["']${escaped}["']\\s*>([\\s\\S]*?)(?=<parameter\\s|$)`,
    'i'
  )
  const openMatch = blob.match(open)
  return openMatch?.[1] !== undefined ? openMatch[1].trim() : undefined
}

/**
 * Split a polluted `question` string into clean question / context / options.
 * Returns the original question unchanged when no leak markers are present.
 */
export function repairLeakedAskUserQuestionXml(question: string): AskUserQuestionFields {
  const trimmed = question.trim()
  if (!hasLeakedAskUserQuestionXml(trimmed)) {
    return { question: trimmed }
  }

  const afterQuestionClose = trimmed.split(/<\/question\s*>/i)
  const cleanQuestion =
    afterQuestionClose.length > 1
      ? (afterQuestionClose[0] ?? '').trim()
      : (trimmed.split(/<parameter\s+name\s*=/i)[0] ?? trimmed).trim()

  const contextRaw = extractXmlParameter(trimmed, 'context')
  const optionsRaw = extractXmlParameter(trimmed, 'options')
  const options = optionsRaw ? coerceAskUserQuestionOptions(optionsRaw) : undefined

  return {
    question: cleanQuestion,
    ...(contextRaw ? { context: contextRaw.trim() } : {}),
    ...(options && options.length > 0 ? { options } : {})
  }
}

/**
 * Normalize ask_user_question tool args (or a persisted `agent_writes.params`
 * slice) into clean card fields. Does not throw on empty question — callers
 * that require one should check themselves.
 */
export function normalizeAskUserQuestionFields(input: unknown): AskUserQuestionFields {
  const record = isPlainObject(input) ? input : {}
  let question = typeof record.question === 'string' ? record.question.trim() : ''
  let options = coerceAskUserQuestionOptions(record.options)
  let context =
    typeof record.context === 'string' && record.context.trim() ? record.context.trim() : undefined

  if (question && hasLeakedAskUserQuestionXml(question)) {
    const repaired = repairLeakedAskUserQuestionXml(question)
    question = repaired.question
    if (!context && repaired.context) context = repaired.context
    if ((!options || options.length === 0) && repaired.options?.length) {
      options = repaired.options
    }
  }

  return {
    question,
    ...(options && options.length > 0 ? { options } : {}),
    ...(context ? { context } : {})
  }
}
